"use client";

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useSyncScheduler } from "./scheduler";

import type { SyncSchedulerCallbacks } from "./scheduler";
import type { SyncContextValue, SyncableDatabase } from "./types";

export type { SyncableDatabase };

export interface WebSyncProviderOptions {
  syncUrl: string;
  /** Hook returning the Comlink DB proxy when ready, or null */
  useDatabaseApi: () => SyncableDatabase | null;
  /** Hook returning auth state */
  useAuth: () => {
    isAuthenticated: boolean;
    getAccessToken: () => string | null;
  };
  /** Trigger sync immediately on online reconnect (default: false) */
  syncOnReconnect?: boolean;
}

export function createWebSyncProvider(options: WebSyncProviderOptions): {
  SyncProvider: React.FC<{ children: React.ReactNode }>;
  useSync: () => SyncContextValue;
} {
  const SyncContext = createContext<SyncContextValue>({
    syncState: "idle",
    lastSyncAt: null,
    error: null,
    isOnline: true,
    triggerSync: () => {},
  });

  function SyncProvider({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, getAccessToken } = options.useAuth();
    const db = options.useDatabaseApi();
    const [syncReady, setSyncReady] = useState(false);
    const [isOnline, setIsOnline] = useState(
      typeof navigator !== "undefined" ? navigator.onLine : true,
    );

    // Keep getAccessToken fresh without adding to useMemo deps
    const getAccessTokenRef = useRef(getAccessToken);
    getAccessTokenRef.current = getAccessToken;

    // Initialize sync engine in worker when authenticated
    useEffect(() => {
      if (isAuthenticated && db && !syncReady) {
        void db.initSync(options.syncUrl);
        setSyncReady(true);
      }
    }, [isAuthenticated, db, syncReady]);

    const callbacks = useMemo<SyncSchedulerCallbacks>(
      () => ({
        doSync: async () => {
          if (!db) throw new Error("Database not ready");
          const token = getAccessTokenRef.current();
          if (!token) throw new Error("No access token");
          return db.sync(token);
        },
        onVisibilityChange: (callback) => {
          function handler() {
            if (document.visibilityState === "visible") callback();
          }
          document.addEventListener("visibilitychange", handler);
          return () => document.removeEventListener("visibilitychange", handler);
        },
        clearSyncState: () => {
          if (db) void db.clearSyncState();
        },
      }),
      [db],
    );

    const { syncState, lastSyncAt, error, triggerSync } = useSyncScheduler(
      isAuthenticated && syncReady && db !== null,
      callbacks,
    );

    // Online/offline detection + optional sync-on-reconnect
    useEffect(() => {
      function handleOnline() {
        setIsOnline(true);
        if (options.syncOnReconnect && isAuthenticated && syncReady) {
          triggerSync();
        }
      }
      function handleOffline() {
        setIsOnline(false);
      }

      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }, [isAuthenticated, syncReady, triggerSync]);

    // Reset syncReady on logout
    useEffect(() => {
      if (!isAuthenticated) {
        setSyncReady(false);
      }
    }, [isAuthenticated]);

    return (
      <SyncContext.Provider value={{ syncState, lastSyncAt, error, isOnline, triggerSync }}>
        {children}
      </SyncContext.Provider>
    );
  }

  function useSync(): SyncContextValue {
    return useContext(SyncContext);
  }

  return { SyncProvider, useSync };
}
